import React, { useImperativeHandle, useState, forwardRef } from 'react';
import { View } from 'react-native';

import { TextPropTypes, ViewPropTypes } from 'deprecated-react-native-prop-types';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { VFText, QuickActionMenu, VFButton } from '@vfgroup-oneplatform/foundation/Components';
import { useTheme } from '@vfgroup-oneplatform/foundation/Components/Themes';

import SohoFrameworkTestKeys from '../../../Utils/SohoFrameworkTestKeys';
import useKeyboardInfo from '../../Hooks/KeyboardAvoiding/KeyboardAvoiding.Hooks';

import styles from './ConfirmationQuickAction.Styles';

function renderPriceInfo({
  price,
  currency,
  isCurrencyFirst = false,
  withPriceVat = false,
  withPriceInfoSubtitle = true,
}) {
  return (
    <View style={styles.priceInfoContainerStyle}>
      <View>
        <VFText
          type="primary"
          i18nKey="soho_plans_add_users_quick_action_price_cost"
          style={styles.priceInfoTitleTextStyle}
        />
        {withPriceInfoSubtitle && (
          <VFText
            type="primary"
            i18nKey="soho_plans_add_users_quick_action_price_based_on"
            style={styles.priceInfoSubtitleTextStyle}
          />
        )}
      </View>

      <View>
        <VFText
          type="primary"
          i18nKey={
            isCurrencyFirst
              ? `${currency}${parseFloat(price)?.toFixed(2)}`
              : `${parseFloat(price)?.toFixed(2)}${currency}`
          }
          style={styles.priceTextStyle}
          testKey={SohoFrameworkTestKeys.ConfirmationQuickActionTotalCostText}
        />
        {withPriceVat ? (
          <VFText
            type="primary"
            i18nKey="soho_plans_add_users_quick_action_price_include_vat"
            style={styles.vatTextStyle}
          />
        ) : null}
      </View>
    </View>
  );
}

const RenderConfirmationBody = forwardRef(
  (
    {
      description,
      descriptionStyle,
      descriptionPlaceHolders,
      hint,
      hintPlaceHolders,
      secondHint,
      withPriceInfo,
      priceInfo,
      children,
      confirmButtonTitle,
      onConfirmPress,
      cancelButtonTitle,
      onCancelPress,
      confirmButtonTextStyle,
      cancelButtonTextStyle,
      withConfirmButton = true,
      confirmButtonDisabled = false,
    },
    ref,
  ) => {
    const showCancelBtn = _.isFunction(onCancelPress);
    const [confirmationButtonDisable, setConfirmationButtonDisable] =
      useState(confirmButtonDisabled);

    useImperativeHandle(ref, () => {
      return {
        setConfirmationButtonDisable: isDisabled => {
          setConfirmationButtonDisable(isDisabled);
        },
      };
    });
    return (
      <>
        <VFText
          i18nKey={description}
          placeHolders={descriptionPlaceHolders}
          style={[styles.descriptionTextStyle, descriptionStyle]}
          testKey={SohoFrameworkTestKeys.ConfirmationQuickAction}
          type="primary"
        />
        {!!hint && (
          <VFText
            i18nKey={hint}
            placeHolders={hintPlaceHolders}
            style={[styles.hintTextStyle, styles.hintStyle(!!secondHint)]}
            type="primary"
          />
        )}
        {!!secondHint && (
          <VFText
            i18nKey={secondHint}
            style={[styles.hintTextStyle, styles.secondHintStyle]}
            type="primary"
          />
        )}
        {withPriceInfo && renderPriceInfo(priceInfo)}
        {children}
        <View style={styles.buttonsContainerStyle}>
          {withConfirmButton && (
            <VFButton
              type="primary"
              title={confirmButtonTitle}
              testKey={SohoFrameworkTestKeys.ConfirmationQuickActionConfirmBtn}
              disabled={confirmationButtonDisable}
              style={styles.confirmButtonStyle(showCancelBtn)}
              textStyle={confirmButtonTextStyle}
              onPress={onConfirmPress}
            />
          )}
          {showCancelBtn && (
            <VFButton
              type="secondary"
              title={cancelButtonTitle}
              style={styles.secondaryButton}
              textStyle={cancelButtonTextStyle}
              onPress={onCancelPress}
              testKey={SohoFrameworkTestKeys.ConfirmationQuickActionCancelBtn}
            />
          )}
        </View>
      </>
    );
  },
);

const ConfirmationQuickAction = ({
  isVisible,
  title,
  withCloseButton,
  description,
  hint,
  secondHint,
  withPriceInfo,
  priceInfo,
  confirmButtonTitle,
  cancelButtonTitle,
  onConfirmPress,
  onCancelPress,
  hintPlaceHolders,
  descriptionPlaceHolders,
  children,
  withConfirmButton,
  cancelButtonTextStyle,
  ...props
}) => {
  const theme = useTheme();
  const { keyboardHeight } = useKeyboardInfo();

  return (
    <QuickActionMenu
      type="secondary"
      isVisible={isVisible}
      title={title}
      withCloseButton={withCloseButton}
      closeModal={onCancelPress}
      headerStyle={styles.headerStyle}
      withKeyboardAvoid
      scrollViewProps={{
        keyboardShouldPersistTaps: 'handled',
      }}
      TitleAccessibilityLabel={SohoFrameworkTestKeys.QuickActionMainTitle}
      {...props}
    >
      <View style={styles.containerStyle(keyboardHeight)}>
        <RenderConfirmationBody
          description={description}
          descriptionPlaceHolders={descriptionPlaceHolders}
          theme={theme}
          hint={hint}
          secondHint={secondHint}
          hintPlaceHolders={hintPlaceHolders}
          withPriceInfo={withPriceInfo}
          priceInfo={priceInfo}
          children={children}
          confirmButtonTitle={confirmButtonTitle}
          onConfirmPress={onConfirmPress}
          cancelButtonTitle={cancelButtonTitle}
          onCancelPress={onCancelPress}
          withConfirmButton={withConfirmButton}
          cancelButtonTextStyle={cancelButtonTextStyle}
        />
      </View>
    </QuickActionMenu>
  );
};

RenderConfirmationBody.propTypes = {
  description: PropTypes.string.isRequired,
  descriptionStyle: ViewPropTypes.style,
  descriptionPlaceHolders: PropTypes.arrayOf(PropTypes.string),
  theme: PropTypes.shape({
    name: PropTypes.string,
    isDark: PropTypes.bool,
    colors: PropTypes.object,
  }),
  hint: PropTypes.string,
  secondHint: PropTypes.string,
  hintPlaceHolders: PropTypes.arrayOf(PropTypes.string),
  withPriceInfo: PropTypes.bool,
  priceInfo: PropTypes.shape({
    price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    currency: PropTypes.string,
    isCurrencyFirst: PropTypes.bool,
    withPriceVat: PropTypes.bool,
  }),
  confirmButtonTitle: PropTypes.string.isRequired,
  cancelButtonTitle: PropTypes.string.isRequired,
  onConfirmPress: PropTypes.func,
  onCancelPress: PropTypes.func,
  children: PropTypes.node,
  confirmButtonDisabled: PropTypes.bool,
  confirmButtonTextStyle: ViewPropTypes.style,
  cancelButtonTextStyle: ViewPropTypes.style,
  withConfirmButton: PropTypes.bool,
};

ConfirmationQuickAction.defaultProps = {
  withCloseButton: true,
  withConfirmButton: true,
};

ConfirmationQuickAction.propTypes = {
  isVisible: PropTypes.bool,
  title: PropTypes.string.isRequired,
  hint: PropTypes.string,
  secondHint: PropTypes.string,
  hintPlaceHolders: PropTypes.arrayOf(PropTypes.string),
  description: PropTypes.string.isRequired,
  descriptionPlaceHolders: PropTypes.arrayOf(PropTypes.string),
  withPriceInfo: PropTypes.bool,
  priceInfo: PropTypes.shape({
    price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    currency: PropTypes.string,
    isCurrencyFirst: PropTypes.bool,
    withPriceVat: PropTypes.bool,
  }),
  confirmButtonTitle: PropTypes.string.isRequired,
  cancelButtonTitle: PropTypes.string.isRequired,
  onConfirmPress: PropTypes.func,
  onCancelPress: PropTypes.func,
  children: PropTypes.node,
  withCloseButton: PropTypes.bool,
  withConfirmButton: PropTypes.bool,
  cancelButtonTextStyle: TextPropTypes.style,
};

export { renderPriceInfo, RenderConfirmationBody };
export default ConfirmationQuickAction;
